import type { ColumnDef } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { Permissions } from "@repo/database";
import { highlightText } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import type { EmployeeGroup } from "~/providers/employee-group-provider";
import { SettingsHeader } from "~/components/layout/settings-header";
import { DataTable } from "~/components/miscellaneous/data-table";
import { DataTableColumnHeader } from "~/components/miscellaneous/data-table-column-header";
import { useEmployeeGroup } from "~/hooks/use-employee-group";
import { useEmployeeGroupManager } from "~/managers/use-employee-group-manager";
import { usePermissionManager } from "~/managers/use-permission-manager";
import { usePermissionStore } from "~/stores/use-permission-store";

export const Route = createFileRoute("/_main/settings/_layout/employee-groups")(
  {
    component: EmployeeGroups,
  },
);
function EmployeeGroups() {
  const { t } = useTranslation("common");

  const { openEmployeeGroupDialog } = useEmployeeGroupManager();

  const { employeeGroups } = useEmployeeGroup();

  const columns: ColumnDef<EmployeeGroup>[] = [
    {
      id: t("name"),
      accessorKey: "name",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("name")} />
      ),
      cell: ({ row: { original: employeeGroup }, table }) => {
        const globalFilter = table.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html:
                highlightText(`${employeeGroup.name}`, globalFilter) ?? "",
            }}
          ></p>
        );
      },
    },
  ];

  const { hasPermission } = usePermissionManager();
  const { currentEmployee } = usePermissionStore();

  const [permission, setPermission] = useState(false);

  useEffect(() => {
    if (!currentEmployee) {
      setPermission(false);
    }
    hasPermission({
      requiredPermission: Permissions.view_employee_group,
      label: t("Permissions.view_employee_groups_label"),
    })
      .then((result) => {
        setPermission(result.permission);
      })
      .catch((e) => console.error(e));
  }, [hasPermission, currentEmployee, t]);

  if (!permission) return null;
  return (
    <>
      <SettingsHeader
        buttonLabel={t("new_employee_group")}
        title={t("employee_groups")}
        buttonAction={() => {
          openEmployeeGroupDialog();
        }}
      />
      <DataTable
        data={employeeGroups}
        columns={columns}
        onSelect={openEmployeeGroupDialog}
      />
    </>
  );
}
